/**
 * BBUATFixturePage.jsx
 *
 * Preview-only admin page — creates (or retrieves) the labelled synthetic
 * Block Booking UAT fixture without any token extraction.
 *
 * Access: Schedule page navigation or direct URL /dev/uat/bb-fixture
 * Auth: handled automatically by the React app's axios interceptor (no manual
 *       token copy/paste required).  The server enforces admin role + gate env var.
 */
import React from 'react';
import api from '../services/api';

export default function BBUATFixturePage() {
  const [state, setState] = React.useState('idle'); // idle | loading | done | error
  const [data,  setData]  = React.useState(null);
  const [err,   setErr]   = React.useState('');

  const handleCreate = async () => {
    setState('loading');
    try {
      const resp = await api.post('/dev/uat/bb-fixture');
      setData(resp.data);
      setState('done');
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        'Unknown error'
      );
      setState('error');
    }
  };

  return (
    <div
      style={{
        maxWidth: 680, margin: '48px auto', padding: '0 24px',
        fontFamily: 'monospace', fontSize: 14,
      }}
      data-testid="bb-uat-fixture-page"
    >
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
        [BB-UAT] Block Booking UAT Fixture
      </h1>
      <p style={{ color: '#555', marginBottom: 24 }}>
        Preview-only · Admin role required · Idempotent (safe to run multiple times)
      </p>

      {state === 'idle' && (
        <button
          onClick={handleCreate}
          data-testid="bb-uat-create-btn"
          style={{
            padding: '10px 22px', background: '#1e293b', color: '#fff',
            border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14,
          }}
        >
          Create / Retrieve Fixture
        </button>
      )}

      {state === 'loading' && (
        <p data-testid="bb-uat-loading">Creating fixture…</p>
      )}

      {state === 'error' && (
        <div data-testid="bb-uat-error" style={{ color: '#b91c1c', marginTop: 16 }}>
          <strong>Error:</strong> {err}
        </div>
      )}

      {state === 'done' && data && (
        <div data-testid="bb-uat-result">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {[
                ['Fixture label',  data.fixture_label],
                ['Site name',      data.site_name],
                ['Site ID',        data.site_id],
                ['Service name',   data.service_name],
                ['Service ID',     data.service_id],
                ['Auditor',        data.auditor_name],
                ['Auditor ID',     data.auditor_id],
                ['Client ID',      data.client_id],
                ['Org ID',         data.org_id],
                ['Org notifications_enabled (read-only)', String(data.org_settings_notifications_enabled)],
                ['Org settings modified by fixture',      String(data.org_settings_modified_by_fixture)],
                ['Suppression mechanism', data.suppression_detail?.mechanism],
              ].map(([label, value]) => (
                <tr key={label} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '6px 0', color: '#6b7280', width: '55%' }}>{label}</td>
                  <td style={{ padding: '6px 0', fontWeight: 600 }}>{value ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{
            marginTop: 24, padding: 16, background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 6,
          }}>
            <strong>UAT Instructions</strong>
            <ol style={{ marginTop: 8, paddingLeft: 20, lineHeight: 1.8 }}>
              <li>Navigate to <strong>Schedule</strong> page.</li>
              <li>Filter / select site: <code>[BB-UAT] Synthetic Block Booking Test Site</code>.</li>
              <li>Click <strong>Block Booking</strong> (date-range icon in the toolbar).</li>
              <li>
                Add ≥2 date pairs with valid (non-reversed) ranges;
                set <code>client_reference = BB-UAT-REF-001</code>.
              </li>
              <li>Submit — verify <strong>two separate jobs</strong> are created (not one per day).</li>
              <li>Re-submit the identical pairs — verify <strong>no duplicate jobs</strong>.</li>
              <li>Inspect both job records: confirm <strong>distinct request_idempotency_key</strong> values.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
